<template>
  <div v-if="!authIsReady" class="auth-loading-screen" aria-live="polite" aria-busy="true">
    <div class="auth-loading-content">
      <i class="material-icons auth-loading-icon spin">sync</i>
      <p class="auth-loading-text">Carregando…</p>
    </div>
  </div>
  <div v-else class="app-layout">
    <a href="#main-content" class="skip-link">Ir para o conteúdo</a>
    <Navbar v-if="showAdminLayout" />
    <header v-else class="top-nav">
      <a href="/" class="top-nav__logo-link">
        <img src="/img/main_logo.png" alt="Rafaela Vieira Lashes" width="180" />
      </a>
    </header>
    <main id="main-content" class="container" :class="{ 'container--public': !showAdminLayout }" tabindex="-1">
      <router-view />
    </main>
    <template v-if="user && showAdminLayout">
      <Drawer :model-value="moreMenuOpen" @update:model-value="setMoreMenuOpen">
        <nav class="nav-drawer-list" aria-label="Mais opções">
          <template v-for="item in overflowNavItems" :key="item.id">
            <router-link
              v-if="item.to"
              :to="item.to"
              class="nav-drawer-item"
              :class="{ selected: route === item.routeName }"
              @click="closeMoreMenu"
            >
              <i class="material-icons" aria-hidden="true">{{ item.icon }}</i>
              <span>{{ item.name }}</span>
            </router-link>
            <button
              v-else-if="item.action === 'logout'"
              type="button"
              class="nav-drawer-item"
              aria-label="Sair"
              @click="closeMoreAndLogout"
            >
              <i class="material-icons" aria-hidden="true">{{ item.icon }}</i>
              <span>{{ item.name }}</span>
            </button>
          </template>
        </nav>
      </Drawer>
      <nav class="bottom-nav" aria-label="Navegação principal">
        <div class="bottom-nav-inner">
          <template v-for="item in primaryNavItems" :key="item.id">
            <router-link
              v-if="item.to"
              :to="item.to"
              class="nav-item"
              :class="{ selected: route === item.routeName }"
            >
              <i class="material-icons" aria-hidden="true">{{ item.icon }}</i>
              <span>{{ item.name }}</span>
            </router-link>
            <button
              v-else-if="item.action === 'logout'"
              type="button"
              class="nav-item"
              style="background: none; border: none; cursor: pointer;"
              aria-label="Sair"
              @click="handleLogout"
            >
              <i class="material-icons" aria-hidden="true">{{ item.icon }}</i>
              <span>{{ item.name }}</span>
            </button>
          </template>
          <button
            v-if="overflowNavItems.length > 0"
            type="button"
            class="nav-item"
            style="background: none; border: none; cursor: pointer;"
            aria-label="Mais opções"
            aria-haspopup="true"
            :aria-expanded="moreMenuOpen"
            @click="openMoreMenu"
          >
            <i class="material-icons" aria-hidden="true">more_horiz</i>
            <span>Mais</span>
          </button>
        </div>
      </nav>
    </template>
    <ToastContainer />
  </div>
</template>

<script>
import Navbar from './components/Navbar.vue';
import Drawer from './components/Drawer.vue';
import ToastContainer from './components/ToastContainer.vue';
import { useUserStore } from '@/stores/user';
import { useRouter } from 'vue-router';
import { computed, ref, watch } from 'vue';
import { getToken, onMessage } from 'firebase/messaging'; // onMessage: foreground only; we forward to SW
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, messaging } from '@/firebase';

export default {
  name: 'App',
  components: { Navbar, Drawer, ToastContainer },
  setup() {
    const userStore = useUserStore();
    const router = useRouter();
    const fcmRegistrationAttempted = ref(false);

    async function registerPushNotifications() {
      if (fcmRegistrationAttempted.value || !messaging) return;
      const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) return;
      fcmRegistrationAttempted.value = true;
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        // Use our FCM service worker so background pushes are handled by firebase-messaging-sw.js
        const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/admin' });
        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: reg,
        });
        if (!token) return;
        const functions = getFunctions(app, 'us-central1');
        const saveFcmToken = httpsCallable(functions, 'saveFcmToken');
        await saveFcmToken({ token });
        // Foreground: FCM delivers to page; forward to SW so it shows the notification (single place for UI)
        onMessage(messaging, (payload) => {
          reg.active?.postMessage({ type: 'FCM_FOREGROUND_MESSAGE', payload });
        });
      } catch (err) {
        console.warn('Push registration failed', err);
      }
    }

    watch(
      () => userStore.user,
      (user) => {
        if (user) {
          registerPushNotifications();
        } else {
          fcmRegistrationAttempted.value = false;
        }
      },
      { immediate: true }
    );

    const handleLogout = () => {
      userStore.logout();
      router.push('/admin/entrar');
    };

    const showAdminLayout = computed(() => {
      const path = router.currentRoute.value.path;
      return path.startsWith('/admin');
    });

    const canShowServices = computed(() => userStore.hasPermission('services:manage'));
    const canShowTeam = computed(() => userStore.hasPermission('users:manage'));
    const canShowReport = computed(() => userStore.hasPermission('reports:read'));
    const canShowRoles = computed(() => userStore.hasPermission('roles:manage'));
    const canShowPaymentMethods = computed(() => userStore.hasPermission('paymentMethods:manage'));
    const canShowInventory = computed(() => userStore.hasPermission('inventory:manage'));
    const canShowFinanceiro = computed(() => userStore.hasPermission('bankTransactions:manage'));
    const canShowClients = computed(() => userStore.hasPermission('clients:manage') || userStore.hasPermission('clients:create'));

    const PRIMARY_COUNT = 4;
    const navItemConfig = computed(() => [
      { id: 'home', to: '/admin/', name: 'Agenda', icon: 'event', routeName: 'Home', visible: true },
      { id: 'clients', to: '/admin/clientes', name: 'Clientes', icon: 'people', routeName: 'Client', visible: canShowClients.value },
      { id: 'report', to: '/admin/relatorio', name: 'Relatório', icon: 'bar_chart', routeName: 'Report', visible: canShowReport.value },
      { id: 'financeiro', to: '/admin/financeiro', name: 'Financeiro', icon: 'account_balance_wallet', routeName: 'Financeiro', visible: canShowFinanceiro.value },
      { id: 'inventory', to: '/admin/estoque', name: 'Estoque', icon: 'inventory_2', routeName: 'Inventory', visible: canShowInventory.value },
      { id: 'services', to: '/admin/servicos', name: 'Serviços', icon: 'spa', routeName: 'Services', visible: canShowServices.value },
      { id: 'paymentMethods', to: '/admin/formas-pagamento', name: 'Pagamentos', icon: 'credit_card', routeName: 'PaymentMethods', visible: canShowPaymentMethods.value },
      { id: 'team', to: '/admin/equipe', name: 'Equipe', icon: 'group', routeName: 'Providers', visible: canShowTeam.value },
      { id: 'roles', to: '/admin/perfis', name: 'Perfis', icon: 'admin_panel_settings', routeName: 'Roles', visible: canShowRoles.value },
      { id: 'logout', action: 'logout', name: 'Sair', icon: 'logout', visible: true },
    ]);
    const visibleNavItems = computed(() => navItemConfig.value.filter((item) => item.visible));
    const primaryNavItems = computed(() => visibleNavItems.value.length > PRIMARY_COUNT + 1 ? visibleNavItems.value.slice(0, PRIMARY_COUNT) : visibleNavItems.value);
    const overflowNavItems = computed(() => visibleNavItems.value.length > PRIMARY_COUNT + 1 ? visibleNavItems.value.slice(PRIMARY_COUNT) : []);
    const moreMenuOpen = ref(false);

    watch(
      () => router.currentRoute.value.path,
      () => {
        moreMenuOpen.value = false;
      }
    );

    const openMoreMenu = () => {
      moreMenuOpen.value = true;
    };

    const closeMoreMenu = () => {
      moreMenuOpen.value = false;
    };

    const setMoreMenuOpen = (value) => {
      moreMenuOpen.value = !!value;
    };

    const closeMoreAndLogout = () => {
      moreMenuOpen.value = false;
      handleLogout();
    };

    return {
      route: computed(() => router.currentRoute.value.name),
      showAdminLayout,
      handleLogout,
      openMoreMenu,
      closeMoreMenu,
      setMoreMenuOpen,
      closeMoreAndLogout,
      primaryNavItems,
      overflowNavItems,
      moreMenuOpen,
      user: computed(() => userStore.user),
      authIsReady: computed(() => userStore.authIsReady),
      canShowServices,
      canShowTeam,
      canShowReport,
      canShowRoles,
      canShowPaymentMethods,
      canShowClients
    };
  },
};
</script>

<style scoped>
.nav-item {
  background: none;
}
button.nav-item {
  width: auto;
}
</style>

<style>
.auth-loading-screen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg);
}

.auth-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);
}

.auth-loading-icon {
  font-size: 40px;
  color: var(--color-accent);
}

.auth-loading-icon.spin {
  animation: auth-loading-spin 1s linear infinite;
}

@keyframes auth-loading-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.auth-loading-text {
  margin: 0;
  font-size: 1rem;
  color: var(--color-text-secondary);
}

.skip-link {
  position: absolute;
  top: -100px;
  left: var(--space-md);
  z-index: 10000;
  padding: var(--space-sm) var(--space-md);
  background: var(--color-surface);
  color: var(--color-accent);
  font-weight: 600;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  transition: top 0.2s ease;
}
.skip-link:focus {
  top: var(--space-md);
}
</style>
